﻿'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface HorizontalScrollerProps {
  children: React.ReactNode;
  ariaLabelLeft: string;
  ariaLabelRight: string;
  className?: string;
  /** Tailwind gap class for the inner flex track. Defaults to `gap-3`. */
  gapClassName?: string;
  /** Tailwind bottom-padding class for the inner flex track. Defaults to `pb-1`. */
  paddingBottomClassName?: string;
}

export function HorizontalScroller({
  children,
  ariaLabelLeft,
  ariaLabelRight,
  className,
  gapClassName = 'gap-3',
  paddingBottomClassName = 'pb-1',
}: HorizontalScrollerProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState<{ canLeft: boolean; canRight: boolean }>({
    canLeft: false,
    canRight: false,
  });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const canLeft = el.scrollLeft > 4;
    const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setScrollState((prev) =>
      prev.canLeft === canLeft && prev.canRight === canRight ? prev : { canLeft, canRight },
    );
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [children, update]);

  const scrollBy = (direction: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(160, el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <div className={`relative min-w-0 ${className ?? ''}`}>
      {scrollState.canLeft && (
        <>
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-linear-to-r from-surface via-surface/80 to-transparent" />
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label={ariaLabelLeft}
            className="absolute top-1/2 left-1 z-20 flex size-7 -translate-y-1/2 items-center justify-center rounded-full border border-border-subtle bg-surface text-text-secondary shadow-none active:scale-95"
          >
            <CaretLeftIcon size={13} weight="bold" />
          </button>
        </>
      )}
      {scrollState.canRight && (
        <>
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-linear-to-l from-surface via-surface/80 to-transparent" />
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label={ariaLabelRight}
            className="absolute top-1/2 right-1 z-20 flex size-7 -translate-y-1/2 items-center justify-center rounded-full border border-border-subtle bg-surface text-text-secondary shadow-none active:scale-95"
          >
            <CaretRightIcon size={13} weight="bold" />
          </button>
        </>
      )}
      <ScrollArea
        className="w-full"
        viewportRef={ref}
        onViewportScroll={update}
        viewportClassName="scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className={`flex w-max ${gapClassName} px-1 ${paddingBottomClassName}`}>
          {children}
        </div>
      </ScrollArea>
    </div>
  );
}
